/**
 * Unified review queue. Same resolver mobile uses — returns the
 * union of confirm cards (pending corrective actions) and score
 * cards (videos the kid watched but the parent hasn't rated yet).
 *
 * Replaces the older `getCorrectiveActionsForProfile` flow on web,
 * which only ever surfaced confirm-equivalents and required client-
 * side filtering to recover the same shape.
 */
import {
  ApolloClient,
  NormalizedCacheObject,
  gql,
  useQuery,
  useMutation,
} from '@apollo/client';
import {
  QueryReviewQueueArgs,
  ReviewQueueCard,
  ReviewQueueResult,
  ScoreVideoResult,
  RetractResult,
  MutationScoreVideoArgs,
  MutationRetractVerdictArgs,
} from '../types/generated/graphql';

export const REVIEW_QUEUE = gql`
  query ReviewQueueWeb($profileId: String!, $cursor: String) {
    reviewQueue(profileId: $profileId, cursor: $cursor) {
      cards {
        id
        kind
        # Score variant
        videoId
        title
        channel
        durationLabel
        thumbnailUrl
        question
        # Confirm variant
        actionId
        actionTitle
        evidence
        severity
        # Shared
        aiReport
        watchedAt
        recommendedAt
        # Confirm chrome
        actionType
        tokenStatus
        imageKind
        skipUntil
      }
      nextCursor
      totalCount
    }
  }
`;

const SCORE_VIDEO = gql`
  mutation ScoreVideoWeb(
    $profileId: String!
    $videoId: String!
    $verdict: String!
  ) {
    scoreVideo(profileId: $profileId, videoId: $videoId, verdict: $verdict) {
      success
      message
    }
  }
`;

const RETRACT_VERDICT = gql`
  mutation RetractVerdictWeb($profileId: String!, $videoId: String!) {
    retractVerdict(profileId: $profileId, videoId: $videoId) {
      success
      partial
      note
      message
    }
  }
`;

// Operations every successful verdict invalidates so the deck
// catches up with what the server now returns. reviewQueue's filter
// (`!ratedVideoIds.has(v.id)`) only re-evaluates when its query
// re-fires — without explicit refetchQueries the next read returns
// the cached pre-vote deck with the just-rated card still in it.
const REFETCH_AFTER_VERDICT = ['ReviewQueueWeb'];

export type WebReviewQueueCard = ReviewQueueCard;

export function useReviewQueue(
  client: ApolloClient<NormalizedCacheObject>,
  profileId: string,
) {
  return useQuery<{reviewQueue: ReviewQueueResult}, QueryReviewQueueArgs>(
    REVIEW_QUEUE,
    {
      variables: {profileId},
      client,
      errorPolicy: 'all',
      // cache-and-network so the page paints cached cards instantly,
      // then syncs once the server response lands. Without this, a
      // navigate-back-after-vote would re-render the pre-vote deck.
      fetchPolicy: 'cache-and-network',
      skip: !profileId,
      notifyOnNetworkStatusChange: true,
    },
  );
}

export function useScoreVideo(client: ApolloClient<NormalizedCacheObject>) {
  return useMutation<{scoreVideo: ScoreVideoResult}, MutationScoreVideoArgs>(
    SCORE_VIDEO,
    {
      client,
      refetchQueries: REFETCH_AFTER_VERDICT,
    },
  );
}

export function useRetractVerdict(
  client: ApolloClient<NormalizedCacheObject>,
) {
  return useMutation<
    {retractVerdict: RetractResult},
    MutationRetractVerdictArgs
  >(RETRACT_VERDICT, {
    client,
    refetchQueries: REFETCH_AFTER_VERDICT,
  });
}
